import { X } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Button, useControlSize, type ControlSize } from "./Button.js";

/**
 * Modal dialog.
 *
 * Behaviour the rest of the product relies on:
 * - labelled by its title and described by its description;
 * - focus moves inside on open, Tab and Shift+Tab cycle within the dialog,
 *   and focus returns to the opener on close;
 * - the rest of the page is hidden from assistive technology while open;
 * - Escape, the close control and a backdrop click all close it — except
 *   while `pending` (a mutation is running), and a `destructive` dialog never
 *   closes on a backdrop click so the choice is always explicit;
 * - dialogs stack: a dialog opened from inside another (an edit sheet asking
 *   "Discard changes?") owns Escape, Tab wrapping and focus while it is up,
 *   hides the dialog beneath it, and hands focus back to that dialog when it
 *   closes — see {@link isTopmostLayer};
 * - motion is a short fade/scale that the reduced-motion rule in base.css
 *   collapses to nothing.
 */

export type DialogTone = "default" | "destructive";
export type DialogSize = "small" | "medium" | "large";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** A mutation is in flight: Escape, close and backdrop dismissal are ignored. */
  pending?: boolean;
  tone?: DialogTone;
  size?: DialogSize;
  /** Element to focus on open instead of the first focusable content control. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  controlSize?: ControlSize;
}

export function Dialog(props: DialogProps) {
  if (!props.open) return null;
  return createPortal(<DialogSurface {...props} />, document.body);
}

/** Footer row for a dialog's action buttons; the primary action goes last. */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="dialog__actions">{children}</div>;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      !element.hasAttribute("hidden") &&
      element.getAttribute("aria-hidden") !== "true",
  );
}

/*
 * Layering. Every open dialog renders a backdrop carrying `data-dialog-layer`
 * straight under <body>; the last one in document order is painted on top and
 * is the only layer that reacts to Escape, wraps Tab and pulls focus back in.
 * Layers open one after another (a child dialog opens from an interaction
 * inside its parent), which is how every composition in the product works.
 */

const LAYER_ATTRIBUTE = "data-dialog-layer";

function isTopmostLayer(backdrop: HTMLElement): boolean {
  const layers = document.querySelectorAll(`[${LAYER_ATTRIBUTE}]`);
  return layers[layers.length - 1] === backdrop;
}

/** How many open dialogs are currently hiding each element this module hid. */
const hiddenBy = new Map<HTMLElement, number>();

/**
 * Hides every other child of <body> — the page and any dialog beneath this
 * one — from assistive technology and interaction. Returns the undo, which
 * reveals an element only once no open dialog hides it any more, so a layer
 * closing underneath another never exposes the page early.
 */
function hideOthers(backdrop: HTMLElement): () => void {
  const hidden: HTMLElement[] = [];
  for (const sibling of Array.from(document.body.children)) {
    if (sibling === backdrop || !(sibling instanceof HTMLElement)) continue;
    const count = hiddenBy.get(sibling) ?? 0;
    if (count === 0) {
      // Something the page hid on its own stays the page's business.
      if (sibling.getAttribute("aria-hidden") === "true") continue;
      sibling.setAttribute("aria-hidden", "true");
      sibling.setAttribute("inert", "");
    }
    hiddenBy.set(sibling, count + 1);
    hidden.push(sibling);
  }
  return () => {
    for (const element of hidden) {
      const remaining = (hiddenBy.get(element) ?? 1) - 1;
      if (remaining > 0) {
        hiddenBy.set(element, remaining);
        continue;
      }
      hiddenBy.delete(element);
      element.removeAttribute("aria-hidden");
      element.removeAttribute("inert");
    }
  };
}

let scrollLocks = 0;
let overflowBeforeLock = "";

/** Stops the page scrolling while any dialog is open. Returns the undo. */
function lockScroll(): () => void {
  if (scrollLocks === 0) {
    overflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  scrollLocks += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks -= 1;
    if (scrollLocks === 0) document.body.style.overflow = overflowBeforeLock;
  };
}

/**
 * Returns focus to the element that had it before the dialog opened — after
 * the whole commit has settled, so that when a parent and child dialog close
 * together the page has been revealed by the time focus lands on the opener
 * (focusing an inert element is a no-op in browsers).
 *
 * It yields whenever focus already rests somewhere real by then: the page
 * moved it deliberately (a newly added row), or another dialog is up and the
 * opener is still hidden beneath it.
 */
function restoreFocusLater(target: HTMLElement | null): void {
  if (!target) return;
  queueMicrotask(() => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active !== document.body &&
      !active.closest("[inert]")
    ) {
      return;
    }
    if (!document.contains(target) || target.closest("[inert]")) return;
    target.focus();
  });
}

function DialogSurface({
  onClose,
  title,
  description,
  children,
  pending = false,
  tone = "default",
  size = "medium",
  initialFocusRef,
  closeLabel = "Close",
  controlSize,
}: DialogProps) {
  const resolvedControlSize = useControlSize(controlSize);
  const titleId = useId();
  const descriptionId = useId();
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Latest callbacks/state for document-level listeners without re-binding them.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const initialFocusTargetRef = useRef(initialFocusRef);
  initialFocusTargetRef.current = initialFocusRef;

  // Mount: remember the opener, hide the page behind, lock scroll, move focus in.
  // Unmount: undo every one of those and return focus to the opener.
  useEffect(() => {
    const backdrop = backdropRef.current;
    const panel = panelRef.current;
    if (!backdrop || !panel) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const revealOthers = hideOthers(backdrop);
    const unlockScroll = lockScroll();

    const initialTarget =
      initialFocusTargetRef.current?.current ??
      (contentRef.current
        ? focusableWithin(contentRef.current)[0]
        : undefined) ??
      closeRef.current ??
      panel;
    initialTarget.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (!isTopmostLayer(backdrop) || pendingRef.current) return;
      event.preventDefault();
      onCloseRef.current();
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!isTopmostLayer(backdrop)) return;
      if (event.target instanceof Node && panel.contains(event.target)) return;
      (focusableWithin(panel)[0] ?? panel).focus();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      revealOthers();
      unlockScroll();
      restoreFocusLater(previouslyFocused);
    };
  }, []);

  const trapTab = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    const backdrop = backdropRef.current;
    // A Tab inside a child dialog also bubbles here through React's tree
    // (portals keep the component hierarchy); only the top layer wraps it.
    if (!panel || !backdrop || !isTopmostLayer(backdrop)) return;
    const focusable = focusableWithin(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement;
    const index =
      active instanceof HTMLElement ? focusable.indexOf(active) : -1;

    if (event.shiftKey) {
      if (index <= 0) {
        event.preventDefault();
        last.focus();
      }
    } else if (index === -1 || index === focusable.length - 1) {
      event.preventDefault();
      first.focus();
    }
  };

  const onBackdropClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (pending || tone === "destructive") return;
    onClose();
  };

  return (
    <div
      ref={backdropRef}
      className="dialog-backdrop"
      data-dialog-layer=""
      data-testid="dialog-backdrop"
      onClick={onBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="dialog"
        data-tone={tone}
        data-size={size}
        tabIndex={-1}
        onKeyDown={trapTab}
      >
        <Button
          ref={closeRef}
          variant="quiet"
          size={resolvedControlSize}
          iconOnly
          icon={<X />}
          className="dialog__close"
          disabled={pending}
          onClick={onClose}
        >
          {closeLabel}
        </Button>
        <div className="dialog__header">
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="dialog__description">
              {description}
            </p>
          ) : null}
        </div>
        <div ref={contentRef} className="dialog__content">
          {children}
        </div>
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: DialogTone;
  /** The confirm request is running: confirm is busy and dismissal is ignored. */
  pending?: boolean;
  /** Extra content between the description and the actions (a reason field, a summary). */
  children?: ReactNode;
  size?: DialogSize;
}

/**
 * A dialog whose only outcomes are confirm or cancel. Destructive tone puts
 * initial focus on the cancel action so Enter never destroys anything by
 * accident; confirming is always its own deliberate click.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  pending = false,
  children,
  size = "small",
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const initialFocusRef = tone === "destructive" ? cancelRef : confirmRef;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      pending={pending}
      tone={tone}
      size={size}
      initialFocusRef={initialFocusRef}
    >
      {children}
      <DialogActions>
        <Button
          ref={cancelRef}
          variant="secondary"
          disabled={pending}
          onClick={onClose}
        >
          {cancelLabel}
        </Button>
        <Button
          ref={confirmRef}
          variant={tone === "destructive" ? "destructive" : "primary"}
          busy={pending}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
